import { useEffect, useState, useRef } from "react";
import {
  Chart as ChartJS,
  LineElement,
  BarElement,
  PointElement,
  ArcElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend,
  Filler,
} from "chart.js";
import { Line, Bar, Doughnut } from "react-chartjs-2";
import { useTimeRange } from "../../../context/TimeRangeContext";
import { formatLabels } from "../../../utils/formatLabels";

ChartJS.register(
  LineElement,
  BarElement,
  PointElement,
  ArcElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend,
  Filler
);

const OverallPerformance = ({ chartType = "line" }) => {
  const chartRef = useRef(null);
  const { timeRange } = useTimeRange();
  const [chartData, setChartData] = useState({ labels: [], datasets: [] });
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [loading, setLoading] = useState(true);

  const colors = [
    "#3366FF",
    "#29b6f6",
    "#66bb6a",
    "#ffa726",
    "#ab47bc",
    "#ef5350",
    "#26c6da",
    "#9ccc65",
  ];

  const fetchPerformanceData = async (filter) => {
    const token = localStorage.getItem("token");
    try {
      const res = await fetch(
        `https://zola.technology/api/company/performance/get-spread-taps/?filter=${filter}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      return await res.json();
    } catch (err) {
      console.error("Error fetching performance data:", err);
      return { periods: [] };
    }
  };

  useEffect(() => {
    setIsDarkMode(document.documentElement.classList.contains("dark"));
  }, []);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      const data = await fetchPerformanceData(timeRange);
      const periods = data?.periods || [];
      const labels = formatLabels(periods, timeRange);
      const values = periods.map((item) => item.taps);

      if (chartType === "doughnut") {
        setChartData({
          labels,
          datasets: [
            {
              label: "Taps",
              data: values,
              backgroundColor: labels.map((_, i) => colors[i % colors.length]),
              borderWidth: 1,
            },
          ],
        });
      } else {
        const ctx = chartRef?.current?.ctx;
        const chartArea = chartRef?.current?.chartArea;

        const gradient =
          chartType === "area" && ctx && chartArea
            ? (() => {
                const g = ctx.createLinearGradient(
                  0,
                  chartArea.bottom,
                  0,
                  chartArea.top
                );
                g.addColorStop(0, "rgba(51, 102, 255, 0.2)");
                g.addColorStop(1, "rgba(51, 102, 255, 0.7)");
                return g;
              })()
            : "rgba(51,102,255,0.2)";

        setChartData({
          labels,
          datasets: [
            {
              label: "Taps",
              data: values,
              borderColor: "#3366FF",
              backgroundColor:
                chartType === "area" ? gradient : "#3366FF",
              tension: 0.4,
              fill: chartType === "area",
              pointRadius: 3,
            },
          ],
        });
      }

      setLoading(false);
    };

    loadData();
  }, [timeRange, chartType]);

  const baseOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "top",
        labels: {
          color: isDarkMode ? "#FFFFFF" : "#333333",
        },
      },
    },
    scales:
      chartType === "doughnut"
        ? {}
        : {
            x: {
              ticks: {
                color: isDarkMode ? "#FFFFFF" : "#333333",
              },
              grid: { display: false },
            },
            y: {
              ticks: {
                color: isDarkMode ? "#FFFFFF" : "#333333",
              },
              grid: {
                color: isDarkMode ? "#555555" : "#e0e0e0",
              },
              beginAtZero: true,
            },
          },
  };

  return (
    <div className="p-6 bg-white dark:bg-gray-800 rounded-lg shadow-md my-8">
      <h2 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">
        Taps Overview
      </h2>
      <div className="w-[95%] md:w-full h-[300px]">
        {loading ? (
          <p className="text-gray-500">Loading...</p>
        ) : chartType === "bar" ? (
          <Bar ref={chartRef} data={chartData} options={baseOptions} />
        ) : chartType === "doughnut" ? (
          <Doughnut data={chartData} options={{ ...baseOptions, cutout: "70%" }} />
        ) : (
          <Line ref={chartRef} data={chartData} options={baseOptions} />
        )}
      </div>
    </div>
  );
};

export default OverallPerformance;
